import { buildAccountTree } from "./accountTree";

/**
 * Opções de status usadas no filtro (modal) e na tabela.
 * O valor vazio ("") representa "todos".
 */
export const STATUS_OPTIONS = [
    { value: "pendente", label: "Pendente" },
    { value: "ok", label: "Conciliado" },
    { value: "incorreto", label: "Conciliado (valor errado)" },
    { value: "sem_valor", label: "Sem nenhum valor" },
];

export const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
}, {});

function getConciliacaoStatus(row) {
    const conciliacao = row?.conciliacao;
    if (!conciliacao || !conciliacao.conciliado) return "pendente";

    const valorConciliado = Number(conciliacao.valor_conciliado);
    const saldoAtual = Number(row?.saldo);
    if (Number.isNaN(valorConciliado) || Number.isNaN(saldoAtual)) return "pendente";

    const toCents = (value) => Math.round(value * 100);
    return toCents(valorConciliado) === toCents(saldoAtual) ? "ok" : "incorreto";
}

function isContaZerada(row) {
    const valores = [
        row?.debito,
        row?.credito,
        row?.movimento_mes,
        row?.saldo,
        row?.saldo_anterior,
    ];
    return valores.every((valor) => Math.round(Number(valor) * 100) === 0);
}

/**
 * Status de uma linha (ordem de prioridade):
 * - "ok":        conciliada e o valor bate com o saldo atual
 * - "incorreto": conciliada, mas o valor difere do saldo atual
 * - "sem_valor": não conciliada e sem nenhum valor (tudo zerado)
 * - "pendente":  não conciliada, mas com algum valor
 */
export function getContaStatus(row) {
    const conciliacao = getConciliacaoStatus(row);
    if (conciliacao === "ok" || conciliacao === "incorreto") return conciliacao;
    if (isContaZerada(row)) return "sem_valor";
    return "pendente";
}

/**
 * Indica se a conta é analítica (CT1_CLASSE = 2) — a única que é conciliada
 * diretamente. As sintéticas (CT1_CLASSE = 1) servem apenas de agrupamento.
 */
export function isAnalitica(row) {
    return Number(row?.classe) === 2;
}

/**
 * Calcula o status de cada conta considerando a hierarquia:
 * - Analítica (classe 2): status próprio (ok / incorreto / sem_valor / pendente).
 * - Sintética (classe 1): "ok" (check verde) somente quando TODAS as analíticas
 *   abaixo dela estiverem conciliadas ("ok"). Se alguma estiver "incorreto",
 *   propaga "incorreto"; caso contrário "pendente" (zeradas não deixam verde).
 *
 * @param {Array<object>} rows - lista plana de contas
 * @returns {Map<string, string>} mapa conta => status
 */
export function buildStatusMap(rows) {
    const list = Array.isArray(rows) ? rows : [];
    const { childrenMap } = buildAccountTree(list);
    const statusMap = new Map();

    const collectAnalyticStatuses = (node, acc) => {
        if (Number(node.classe) === 2) {
            acc.push(getContaStatus(node));
        }
        (childrenMap.get(node.conta) || []).forEach((child) =>
            collectAnalyticStatuses(child, acc)
        );
        return acc;
    };

    list.forEach((row) => {
        if (Number(row.classe) === 1) {
            const statuses = [];
            (childrenMap.get(row.conta) || []).forEach((child) =>
                collectAnalyticStatuses(child, statuses)
            );

            let status;
            if (statuses.length === 0) {
                status = "pendente";
            } else if (statuses.every((s) => s === "ok")) {
                status = "ok";
            } else if (statuses.some((s) => s === "incorreto")) {
                status = "incorreto";
            } else {
                status = "pendente";
            }

            statusMap.set(row.conta, status);
        } else {
            statusMap.set(row.conta, getContaStatus(row));
        }
    });

    return statusMap;
}
